import styled from '@emotion/styled';
import { Input } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/shared/components/Button';
import { color, font } from 'src/shared/styles/variables';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto',
    maxWidth: 570,
    maxHeight: 1000,
});

const Title = styled.h3({
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 20,
    color: color.pickledBluewood,
    ...font.black(font.size.header),
});

const Description = styled.h5({
    textAlign: 'center',
    color: color.steelGray,
    lineHeight: '28px',
    marginBottom: 25,
    ...font.book(font.size.mediumExtended),
});

const TextArea = styled(Input.TextArea)({
    '&&': {
        maxWidth: 510,
        marginTop: 16,
        padding: 11,
        border: `2px solid ${color.linkWaterLight}`,
        color: color.blueBayoux,
        ...font.medium(font.size.base),
    },
});

const ButtonWrapper = styled.div({
    maxWidth: 510,
    marginTop: 32,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
});

interface Props {
    onSubmit: (text: string) => void;
    onBackClick: () => void;
}

export enum LocalTherapyRequestTestId {
    Continue = 'LocalTherapyRequestTestId.Continue',
    Back = 'LocalTherapyRequestTestId.Back',
}

export const LocalTherapyRequest: React.FC<Props> = ({ onSubmit, onBackClick }) => {
    const { t } = useTranslation();
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setSubmitting(true);

        await onSubmit(text);

        setSubmitting(false);
    };

    return (
        <Wrapper>
            <Title>{t('providers:local_therapy.title')}</Title>
            <Description>{t('providers:local_therapy.description.1')}</Description>

            <Description>{t('providers:local_therapy.description.2')}</Description>

            <TextArea
                rows={5}
                placeholder={t('providers:local_therapy.anything_else')}
                value={text}
                onChange={({ target }) => setText(target.value)}
            />

            <ButtonWrapper>
                <Button
                    testID={LocalTherapyRequestTestId.Back}
                    onClick={onBackClick}
                    type="white"
                    widthPercentage={49}
                    withShadow
                >
                    {t('common.back')}
                </Button>
                <Button
                    testID={LocalTherapyRequestTestId.Continue}
                    onClick={handleSubmit}
                    loading={submitting}
                    widthPercentage={49}
                    withShadow
                >
                    {t('common.continue')}
                </Button>
            </ButtonWrapper>
        </Wrapper>
    );
};
